import { TaleemabadCapacitorPlugin } from 'taleemabad-capacitor-plugin';
import { teacherTrainingDB } from '../models/teachertraining';
import {
  DownloadInfo,
  DownloadListener,
  DownloadRequest,
  DownloadResponse,
  DownloadStatus,
} from '../types/downloads';
import { Capacitor } from '@capacitor/core';
import { ITraining } from '../types/teachertraining';
import { Directory, Filesystem } from '@capacitor/filesystem';

export function downloadParser(download: { result: string }): DownloadInfo {
  const jsonString = JSON.stringify(download);
  const downloadResponse: DownloadResponse = JSON.parse(jsonString);
  const downloadInfo = JSON.parse(downloadResponse.download);
  if (downloadInfo.tag) {
    downloadInfo.tag = JSON.parse(downloadInfo.tag);
  }
  return downloadInfo;
}

export function downloadListParser(response: {
  value: string;
}): DownloadInfo[] {
  const jsonString = JSON.stringify(response);
  const downloadResponse: DownloadResponse = JSON.parse(jsonString);
  const downloadInfo = JSON.parse(downloadResponse.download).map(
    (info: any) => {
      if (info.tag) {
        info.tag = JSON.parse(info.tag);
      }
      return { ...info };
    },
  );

  return downloadInfo;
}

class DownloadManagerService {
  private static instance: DownloadManagerService;
  private eventTarget = new EventTarget();
  public constructor() {
    if (Capacitor.isNativePlatform()) {
      this.addOnListener([
        DownloadListener.OnAdded,
        DownloadListener.OnProgress,
        DownloadListener.OnCompleted,
        DownloadListener.OnError,
        DownloadListener.OnRemoved,
        DownloadListener.OnDeleted,
        DownloadListener.onCancelled,
      ]);
    }
  }

  public static getInstance(): DownloadManagerService {
    if (!DownloadManagerService.instance) {
      DownloadManagerService.instance = new DownloadManagerService();
    }
    return DownloadManagerService.instance;
  }

  public async getVideoURL(training: ITraining): Promise<string | null> {
    if (training.mediaAsset?.download?.status === DownloadStatus.Completed) {
      try {
        const filename = training.mediaAsset.download.url.substring(
          training.mediaAsset.download.url.lastIndexOf('/') + 1,
        );
        const fileUri = await Filesystem.getUri({
          path: filename,
          directory: Directory.Library,
        });
        return Capacitor.convertFileSrc(fileUri.uri);
      } catch (e) {
        console.log('Error getting video URL :: ' + e);
        return null;
      }
    }
    return null;
  }

  public async getDownloadURL(url: string): Promise<string | null> {
    try {
      const filename = url.substring(url.lastIndexOf('/') + 1);
      const fileUri = await Filesystem.getUri({
        path: filename,
        directory: Directory.Library,
      });
      return Capacitor.convertFileSrc(fileUri.uri);
    } catch (e) {
      console.log('Error getting URL :: ' + e);
      return null;
    }
  }

  public downloadMediaAsset(
    request: DownloadRequest[],
    isDownloadWithTag: Boolean = false,
  ) {
    try {
      if (isDownloadWithTag) {
        const mappedArray = request.map((req) => {
          const tag = JSON.stringify({
            mediaType: req.mediaType,
            modelDownloadName: req.modelDownloadName,
            modelDownloadRequestId: req.modelDownloadRequestId,
            modelDownloadAddedInformation: req?.modelDownloadAddedInformation,
            timestamp: req.timestamp,
          });

          return {
            tag,
            url: req.url,
          };
        });
        TaleemabadCapacitorPlugin.startDownloadWithTag({
          url: mappedArray,
        });
      } else {
        TaleemabadCapacitorPlugin.startDownload({ url: [request[0].url] });
      }
    } catch (error) {
      console.log('Error while downloading file');
    }
  }

  public async deleteDownload(request: DownloadRequest[]) {
    await Promise.all(
      request.map(async (req) => {
        try {
          if (req?.download?.id) {
            await TaleemabadCapacitorPlugin.removeDownloads({
              value: [req.download.id?.toString()],
            });
          } else console.log('No download ID found!');
        } catch (error) {
          console.log('Error while deleting download!');
        }
      }),
    );
  }

  public async getDownloadsFromNative(resume: boolean = true): Promise<void> {
    try {
      await TaleemabadCapacitorPlugin.getDownloadList().then(
        async (response) => {
          const downloadList = downloadListParser(response);

          if (downloadList.length > 0) {
            try {
              // Use dynamic imports to avoid circular dependencies
              const { lessonPlanDB } = await import('../models/lessonplan');

              await lessonPlanDB.updateLessonPlanDetailsWithDownloads(
                downloadList,
              );
              // todo to update the training with downloads from native and will create method to send download on all the relavent models.
              await teacherTrainingDB.updateTrainingsWithDownloads(
                downloadList,
              );
              if (resume) {
                this.resumePendingDownloads();
              }
            } catch (e) {
              throw e;
            }
          } else console.log('No files downloaded!');
        },
      );
    } catch (error) {
      console.log('Error getting download list :: ' + error);
      throw error;
    }
  }

  public async resumePendingDownloads(): Promise<void> {
    try {
      await TaleemabadCapacitorPlugin.resumeDownloads();
    } catch (error) {
      console.log('Error resuming download list :: ' + error);
      throw error;
    }
  }

  public async syncDownloads(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      // sync downloads from native platforms
      await this.getDownloadsFromNative();
    }
  }

  private async handleDownloadEvents(
    download: { result: string },
    downloadListner: DownloadListener,
  ) {
    const downloadInfo = downloadParser(download);

    if (
      downloadListner === DownloadListener.OnRemoved ||
      downloadListner === DownloadListener.OnDeleted
    ) {
      return;
    }

    this.dispatchDownloadStatus(downloadInfo);
  }
  public async pauseDownload(request: DownloadRequest[]) {
    await Promise.all(
      request.map(async (req) => {
        try {
          if (req?.download?.id) {
            await TaleemabadCapacitorPlugin.pauseDownloads({
              value: [req.download.id?.toString()],
            });
          } else console.log('No download ID found!');
        } catch (error) {
          console.log('Error while pausing download!');
        }
      }),
    );
  }

  private addOnListener(listeners: DownloadListener[]) {
    listeners.forEach((listener) => {
      TaleemabadCapacitorPlugin.addListener(listener, async (download) => {
        try {
          this.handleDownloadEvents(download, listener);
        } catch (error) {
          if (listener === DownloadListener.OnError) {
            console.log('Error handling download event:', error);
          }
        }
      });
    });
  }

  private dispatchDownloadStatus(download: DownloadInfo) {
    const event = new CustomEvent('onDownload', { detail: download });
    this.eventTarget.dispatchEvent(event);
  }

  private listenerWrappers = new Map<
    (download: DownloadInfo) => void,
    (event: Event) => void
  >();

  public onDownload(callback: (download: DownloadInfo) => void) {
    if (this.listenerWrappers.has(callback)) return;
    const wrapper = (event: Event) => {
      callback((event as CustomEvent).detail);
    };
    this.listenerWrappers.set(callback, wrapper);
    this.eventTarget.addEventListener('onDownload', wrapper);
  }

  public removeDownloadListener(callback: (download: DownloadInfo) => void) {
    const wrapper = this.listenerWrappers.get(callback);
    if (wrapper) {
      this.eventTarget.removeEventListener('onDownload', wrapper);
      this.listenerWrappers.delete(callback);
    }
  }
}

export const downloadManagerService = DownloadManagerService.getInstance();
